/**
 * publish-lane-guard — A1. The ONE list of transition-controlled article fields, and the
 * refusal the generic writers raise when a caller tries to set one.
 *
 * WHY THIS EXISTS (defect register §A1, 2026-08-06/07)
 *   Three articles reached `status:'published'` with `transitionLog === undefined` — i.e.
 *   the write never passed through an audited transition at all. The writer was the
 *   CLIENT-SIDE generic updater `ArticlesService.updateArticle`, reachable from three
 *   doors: the list-row publish button (`useArticleActions.ts`), the editor form
 *   (`ArticleForm.tsx`), and `PUT /api/articles/[id]` (which forwarded an arbitrary JSON
 *   body with no allow/deny list). The backend operator lane has had exactly this guard
 *   since the publish-authority endgame — `backend/scripts/ops.js:556`:
 *
 *       if ('status' in fields) {
 *         out('ERROR: refusing to patch "status" via patch-field — use publish-reviewed / hold / reject / backfill-publish');
 *         return { exitCode: 2, results };
 *       }
 *
 *   This module is that guard, mirrored onto the browser plane.
 *
 * REFUSE, NOT STRIP. ops.js refuses loudly (exit 2) and names the verbs that own the
 *   field. We do the same, for a reason specific to this defect: a SILENT STRIP would
 *   make the dashboard's Publish button appear to work while writing nothing — trading a
 *   visible unaudited publish for an invisible no-op, which is strictly worse to diagnose.
 *   A caller that gets `TransitionFieldRefusedError` is told which lane owns the field.
 *
 * WHAT IS "OWNED" (verified against the code, not assumed)
 *   `PublishService.applyTransition` (backend/src/services/PublishService.js:282-296)
 *   writes exactly `status`, `transition`, `transitionBy`, `transitionAt`,
 *   `transitionLog`; its Next-plane mirror `transitionAudit`
 *   (src/lib/server/publish-transition.ts) writes the same four audit fields. Those five
 *   are owned outright. `publishedAt` / `scheduledPublishAt` are composed by the callers
 *   of that lane (the drip at PublishSchedulerService.js:279, the approve route, ops.js
 *   `publish-reviewed`) and are meaningless outside a transition, so an editorial form
 *   bag must not carry them either — no frontend form sends them today (grep-verified).
 *
 * WHAT IS DELIBERATELY *NOT* OWNED: `visibility`.
 *   PublishService's own header (:26-29) lists `visibility` among the "status-adjacent
 *   fields … written by the caller exactly as before" — applyTransition never touches it.
 *   It is also a live editorial control (the ArticleForm visibility dropdown,
 *   ArticleForm.tsx:1348 — public/preview/unlisted/hidden), so refusing it would break a
 *   non-transition edit. The `published` + `hidden` state seen on the three evidence docs
 *   is fixed where it is actually caused: the audited publish route promotes
 *   hidden → public exactly like the drip does (PublishSchedulerService.js:289).
 */

/** Written by `applyTransition` / `transitionAudit` themselves. Never caller-supplied. */
export const TRANSITION_AUDIT_FIELDS = [
  'transition',
  'transitionBy',
  'transitionAt',
  'transitionLog',
] as const;

/** The publish state and its timestamps — only ever written alongside an audited hop. */
export const PUBLISH_STATE_FIELDS = ['status', 'publishedAt', 'scheduledPublishAt'] as const;

/**
 * THE list. Every generic article writer (service update, service create, the PUT route)
 * refuses these; the audited lane below is the only thing allowed to write them.
 */
export const TRANSITION_OWNED_FIELDS: readonly string[] = [
  ...PUBLISH_STATE_FIELDS,
  ...TRANSITION_AUDIT_FIELDS,
];

/** The audited egress the refusal points callers at. */
export const AUDITED_ARTICLE_TRANSITION_ROUTE = '/api/articles/transition';

/**
 * Statuses a brand-new article may NOT be created directly into. `∅→published` is a legal
 * row in the shared table (backfill scripts use it) but it still lands a live doc with no
 * `transitionLog`, so the generic creator refuses it: create the draft, then transition.
 */
export const CREATION_REFUSED_STATUSES: readonly string[] = ['published', 'scheduled'];

/** Thrown by the generic writers. `fields` names every offending key, ops.js-style. */
export class TransitionFieldRefusedError extends Error {
  readonly code = 'TRANSITION_FIELD_REFUSED';
  readonly fields: string[];

  constructor(fields: string[], detail: string) {
    super(
      `Refusing to write ${fields.map((f) => `"${f}"`).join(', ')} via a generic article update — ` +
        `${detail} Use the audited transition lane (POST ${AUDITED_ARTICLE_TRANSITION_ROUTE}), ` +
        `which stamps transitionLog. (Mirror of backend/scripts/ops.js:556.)`,
    );
    this.name = 'TransitionFieldRefusedError';
    this.fields = fields;
  }
}

export interface TransitionGuardOptions {
  /**
   * The doc's CURRENT status. `status` is refused only when it is a real CHANGE — admin
   * forms echo the current status back on every content save, and those must stay
   * byte-identical flows. Omit (with `creation: true`) for the create path.
   */
  currentStatus?: string | null;
  /** Create path: refuse only the landing states in `CREATION_REFUSED_STATUSES`. */
  creation?: boolean;
}

/**
 * Names every transition-controlled field the payload is trying to write. Pure; returns
 * `[]` for an ordinary content edit.
 */
export function findTransitionOwnedFields(
  data: Record<string, unknown> | null | undefined,
  opts: TransitionGuardOptions = {},
): string[] {
  if (!data || typeof data !== 'object') return [];
  const offenders: string[] = [];

  // `status` — change-sensitive (see TransitionGuardOptions.currentStatus).
  const status = (data as { status?: unknown }).status;
  if (status !== undefined) {
    if (opts.creation) {
      if (typeof status === 'string' && CREATION_REFUSED_STATUSES.includes(status)) {
        offenders.push('status');
      }
    } else if (status !== opts.currentStatus) {
      offenders.push('status');
    }
  }

  // Everything else is refused on PRESENCE — no editorial form carries these.
  for (const field of TRANSITION_OWNED_FIELDS) {
    if (field === 'status') continue;
    if ((data as Record<string, unknown>)[field] !== undefined) offenders.push(field);
  }

  return offenders;
}

/** `findTransitionOwnedFields`, but throws `TransitionFieldRefusedError` on any hit. */
export function assertNoTransitionOwnedFields(
  data: Record<string, unknown> | null | undefined,
  opts: TransitionGuardOptions = {},
): void {
  const offenders = findTransitionOwnedFields(data, opts);
  if (offenders.length === 0) return;

  const detail = opts.creation
    ? `a new article cannot be created directly into "${String((data as { status?: unknown })?.status)}".`
    : offenders.includes('status')
      ? `a status change (${String(opts.currentStatus ?? '∅')} → ${String((data as { status?: unknown })?.status)}) is an audited transition, not a content edit.`
      : 'these fields are written only by the audited transition lane.';

  throw new TransitionFieldRefusedError(offenders, detail);
}
